import type { CSSProperties } from "react";
import type { IconType } from "react-icons";
import {
  SiCisco,
  SiSalesforce,
  SiVerizon,
  SiZoho,
  SiZendesk,
  SiSap,
  SiBankofamerica,
  SiJavascript,
} from "react-icons/si";

/* ── Company-logo registry (port of the prototype's logos.js CO_LOGOS) ────
   Maps experience id → an HTML snippet (inline SVG, <img>, or styled
   initials). Every view (Eras road, Timeline, Graph, detail pages) renders
   company marks through BrandLogo, which reads THIS map first, then the
   monogram fallbacks. Add or swap a company's mark here and it changes
   everywhere at once. All marks are self-hosted: inline SVG vectors or
   bitmaps under /public/logos — no Wikimedia/favicon-service hotlinks
   (Blue Cross NC is the one sanctioned exception until an official file
   is supplied).

   Safety note: these snippets are rendered via dangerouslySetInnerHTML.
   That is safe here because every string is a static constant defined in
   this module — props only ever SELECT a registry entry by key; no caller
   input is interpolated into the HTML. Keep it that way: never build a
   registry value from runtime data. */

const svg = (inner: string, viewBox = "0 0 100 100") =>
  `<svg viewBox="${viewBox}" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">${inner}</svg>`;
const img = (src: string, alt: string) =>
  `<img src="${src}" alt="${alt}" loading="lazy" style="width:100%;height:100%;object-fit:contain;display:block;" />`;

// Cisco — signature vertical "bridge" bars
const cisco = (() => {
  const blue = "#1BA0D7";
  const heights = [22, 38, 22, 38, 22, 38, 22, 38, 22];
  const bw = 5,
    gap = 4.6;
  const total = heights.length * bw + (heights.length - 1) * gap;
  let x = (100 - total) / 2;
  let bars = "";
  for (const h of heights) {
    const y = 50 - h / 2;
    bars += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw}" height="${h}" rx="2.4" fill="${blue}"/>`;
    x += bw + gap;
  }
  return svg(bars);
})();

// Deloitte — black "D" with the signature green dot
const deloitte = svg(
  `<text x="42" y="50" dy="0.34em" text-anchor="middle" font-family="Hanken Grotesk, Arial, sans-serif" font-size="62" font-weight="700" fill="#000">D</text>` +
    `<circle cx="74" cy="63" r="8" fill="#86BC25"/>`,
);

// IBM — 8-bar wordmark, inlined from the same vector the prototype hotlinked
// (Wikimedia IBM_logo.svg), registered-mark glyph omitted at tile sizes.
const ibm = svg(
  `<g fill="#1f70c1">` +
    `<rect y="373.17" width="194.43" height="27.932"/><rect y="319.83" width="194.43" height="27.932"/><rect x="55.468" y="266.54" width="83.399" height="27.932"/><rect x="55.468" y="213.25" width="83.399" height="27.932"/><rect x="55.468" y="159.96" width="83.399" height="27.932"/><rect x="55.468" y="106.58" width="83.399" height="27.932"/><rect y="53.288" width="194.43" height="27.932"/><rect width="194.43" height="27.932"/>` +
    `<path d="m222.17 400.85 207.11 0.297c27.734 0 52.793-10.697 71.513-27.932h-278.62z"/><path d="m222.17 347.76h299.03c5.051-8.617 8.815-18.027 11.094-27.932h-310.12z"/><rect x="277.73" y="266.54" width="83.3" height="27.932"/><path d="m444.43 266.54v27.932h90.927c0-9.608-1.288-19.017-3.764-27.932z"/><path d="m497.92 213.25h-220.19v27.932h243.46c-6.34-10.698-14.165-20.107-23.277-27.932z"/><path d="m277.73 159.96v27.932h220.19c9.311-7.825 17.135-17.235 23.277-27.932z"/><rect x="277.73" y="106.58" width="83.3" height="27.932"/><path d="m444.43 134.51h87.163c2.476-8.914 3.764-18.324 3.764-27.932h-90.927z"/><path d="m521.2 53.288h-299.03v27.932h310.12c-2.575-9.905-6.339-19.314-11.093-27.932z"/><path d="m429.28 0h-207.11v27.932h278.53c-18.621-17.235-43.878-27.932-71.414-27.932z"/>` +
    `<polygon points="555.57 81.22 742.67 81.22 733.06 53.288 555.57 53.288"/><polygon points="555.57 27.932 724.25 27.932 714.64 0 555.57 0"/><polygon points="861.03 401.17 861.03 373.24 1000 373.24 1000 401.17"/><polygon points="861.03 347.76 861.03 319.83 1000 319.83 1000 347.76"/><polygon points="777.73 182.54 769.91 159.96 694.43 159.96 611.03 159.96 611.03 187.89 694.43 187.89 694.43 162.24 703.25 187.89 852.22 187.89 861.03 162.24 861.03 187.89 944.43 187.89 944.43 159.96 861.03 159.96 785.56 159.96"/><polygon points="944.43 106.58 803.98 106.58 794.37 134.51 944.43 134.51"/><polygon points="1000 27.932 1000 0 840.93 0 831.32 27.932"/><polygon points="768.13 373.22 777.73 400.85 787.34 373.22"/><polygon points="749.5 319.83 759.31 347.76 796.16 347.76 806.06 319.83"/><polygon points="730.78 266.54 740.59 294.47 814.88 294.47 824.68 266.54"/><polygon points="721.97 241.18 833.6 241.18 843.11 213.25 712.36 213.25"/><polygon points="611.03 134.51 761.09 134.51 751.49 106.58 611.03 106.58"/><polygon points="1000 53.288 822.4 53.288 812.9 81.22 1000 81.22"/><rect x="555.57" y="373.22" width="138.97" height="27.932"/><rect x="555.57" y="319.83" width="138.97" height="27.932"/><rect x="611.03" y="266.54" width="83.399" height="27.932"/><rect x="611.03" y="213.25" width="83.399" height="27.932"/><rect x="861.03" y="213.25" width="83.399" height="27.932"/><rect x="861.03" y="266.54" width="83.399" height="27.932"/>` +
    `</g>`,
  "0 0 1000 401.15",
);

// Verizon — 2024 V-check symbol, inlined from the same vector the prototype
// hotlinked (Wikimedia Verizon_2024_(symbol).svg), gradient included.
const verizon = svg(
  `<defs><radialGradient id="brand-vz-grad" cx="-227.379" cy="164.06917" fx="-227.379" fy="164.06917" r="30.251394" gradientTransform="matrix(9.6100483e-7,-3.7650134,3.4156819,8.7183933e-7,-705.50063,-739.98397)" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#f5ff1e"/><stop offset="1" stop-color="#f50a23"/></radialGradient></defs>` +
    `<g transform="translate(276.06152,41.207843)">` +
    `<path d="m -276.06152,-41.207843 91.01924,237.084863 c 0.69179,1.80356 2.42124,2.9895 4.34836,2.9895 h 73.84813 c -1.92712,0 -3.65657,-1.18594 -4.34836,-2.9895 l -91.01924,-237.084863 z" fill="url(#brand-vz-grad)"/>` +
    `<path d="M -11.478187,-41.207843 -102.49743,195.87702 c -0.69176,1.80356 -2.42123,2.9895 -4.34836,2.9895 h -73.84813 c 1.92713,0 3.6566,-1.18594 4.34837,-2.9895 l 91.019234,-237.084863 z" fill="#f50a23"/>` +
    `</g>`,
  "0 0 264.58334 240.07435",
);

// Author-supplied bitmaps, self-hosted from /public/logos
const bofa = img("/logos/bofa-flagscape.png", "Bank of America"); // flagscape, supplied by Srinath
const metlife = img("/logos/metlife-mark.png", "MetLife"); // official M mark
const startec = img("/logos/startec.png", "Startec"); // star mark, supplied by Srinath (16px source)

// Blue Cross NC — their media center blocks hotlinking; best public 48px
// icon, falling back to a typographic monogram (no second favicon hotlink).
// Swap in an uploaded official file when available.
const bcbsnc =
  `<img src="https://icons.duckduckgo.com/ip3/bluecrossnc.com.ico" alt="Blue Cross NC" loading="lazy" style="width:100%;height:100%;object-fit:contain;display:block;" onerror="this.onerror=null;this.style.display='none';if(this.nextElementSibling)this.nextElementSibling.style.display='grid'" />` +
  `<span aria-hidden="true" style="display:none;place-items:center;width:100%;height:100%;font-weight:800;font-size:0.8em;letter-spacing:0.02em;color:#0284c7">BC</span>`;

// Cybernet — no surviving logo; styled "CSS" initials as the placeholder mark.
const cybernet = `<span style="display:grid;place-items:center;width:100%;height:100%;font-family:'JetBrains Mono',monospace;font-weight:800;font-size:11px;letter-spacing:0.04em;color:var(--muted-strong)">CSS</span>`;

export const CO_LOGOS: Record<string, string> = {
  deloitte,
  cisco,
  metlife,
  "bcbs-nc": bcbsnc,
  "bofa-merrill": bofa,
  "verizon-business": verizon,
  "ibm-gs": ibm,
  "ibm-sg": ibm,
  "ibm-pcd": ibm,
  "ibm-tvm": ibm,
  startec,
  cybernet,
};

/* Company display names → registry ids, so call sites may pass either the
   experience id or the company name from resume.yaml ("IBM PCD",
   "Bank of America ML", "Startec Global Comm.", …). */
const NAME_PATTERNS: Array<[RegExp, string]> = [
  [/^deloitte/, "deloitte"],
  [/^cisco/, "cisco"],
  [/^metlife/, "metlife"],
  [/^blue ?cross/, "bcbs-nc"],
  [/bank of america|merrill/, "bofa-merrill"],
  [/^verizon/, "verizon-business"],
  [/^ibm\b/, "ibm-gs"],
  [/^startec/, "startec"],
  [/^cybernet/, "cybernet"],
  [/^sj enterprise/, "sj-enterprise"],
];

function resolveBrandKey(raw: string): string {
  const k = raw.trim().toLowerCase();
  if (CO_LOGOS[k] || ICON[k] || MONOGRAM[k]) return k;
  for (const [re, id] of NAME_PATTERNS) if (re.test(k)) return id;
  return k;
}

interface BrandEntry {
  Icon?: IconType;
  hex?: string;
  // Typographic fallback
  letters?: string;
  hue?:
    | "stone"
    | "slate"
    | "zinc"
    | "amber"
    | "orange"
    | "emerald"
    | "teal"
    | "sky"
    | "indigo"
    | "violet"
    | "fuchsia"
    | "rose";
}

// ── Real Simple-Icons brand logos (technologies) ─────────────────────────
const ICON: Record<string, BrandEntry> = {
  cisco: { Icon: SiCisco, hex: "#1BA0D7" },
  salesforce: { Icon: SiSalesforce, hex: "#00A1E0" },
  "salesforce-marketing-cloud": { Icon: SiSalesforce, hex: "#00A1E0" },
  "bofa-merrill": { Icon: SiBankofamerica, hex: "#012169" },
  "verizon-business": { Icon: SiVerizon, hex: "#EE0000" },
  zoho: { Icon: SiZoho, hex: "#E42527" },
  zendesk: { Icon: SiZendesk, hex: "#03363D" },
  "sap-hana": { Icon: SiSap, hex: "#0FAAFF" },
  "sap-rfc": { Icon: SiSap, hex: "#0FAAFF" },
  javascript: { Icon: SiJavascript, hex: "#F7DF1E" },
};

// ── Typographic monograms (no logo available / not in the registry) ──────
const MONOGRAM: Record<string, BrandEntry> = {
  // Companies
  deloitte: { letters: "D.", hue: "emerald" },
  "sj-enterprise": { letters: "SJ", hue: "slate" },
  metlife: { letters: "M", hue: "indigo" },
  "bcbs-nc": { letters: "BC", hue: "sky" },
  "ibm-gs": { letters: "IBM", hue: "indigo" },
  "ibm-sg": { letters: "IBM", hue: "indigo" },
  "ibm-pcd": { letters: "IBM", hue: "indigo" },
  "ibm-tvm": { letters: "IBM", hue: "indigo" },
  startec: { letters: "St", hue: "zinc" },
  cybernet: { letters: "Cy", hue: "stone" },

  // AWS / Amazon family
  "amazon-connect": { letters: "AC", hue: "amber" },
  "amazon-q-connect": { letters: "Q", hue: "amber" },
  "aws-bedrock": { letters: "Br", hue: "amber" },
  "amazon-lex": { letters: "Lx", hue: "amber" },
  "amazon-contact-lens": { letters: "CL", hue: "amber" },
  "amazon-chime": { letters: "Cm", hue: "amber" },
  "aws-glue": { letters: "Gl", hue: "amber" },
  "amazon-athena": { letters: "At", hue: "amber" },
  "amazon-quicksight": { letters: "QS", hue: "amber" },
  "aws-clean-room": { letters: "CR", hue: "amber" },
  "amazon-sagemaker": { letters: "SM", hue: "amber" },

  // ServiceNow
  servicenow: { letters: "Sn", hue: "emerald" },

  // IBM-family techs
  websphere: { letters: "IBM", hue: "indigo" },
  "websphere-portal": { letters: "IBM", hue: "indigo" },
  "ibm-maximo": { letters: "Mx", hue: "indigo" },
  "aix-vio": { letters: "AIX", hue: "indigo" },
  "lotus-ei": { letters: "L", hue: "indigo" },
  "lotus-notes": { letters: "L", hue: "indigo" },

  // Oracle / Adobe / Java
  "oracle-db": { letters: "O", hue: "rose" },
  java: { letters: "J", hue: "orange" },
  "adobe-pdf-sdk": { letters: "A", hue: "rose" },

  // Concepts (not brand-named)
  "amdocs-connectx": { letters: "AC", hue: "indigo" },
  "xml-xsl": { letters: "XML", hue: "amber" },
  "ai-ml": { letters: "AI", hue: "fuchsia" },
  "llm-search": { letters: "LLM", hue: "fuchsia" },
  checkr: { letters: "Ch", hue: "emerald" },
  "hybrid-cloud": { letters: "HC", hue: "zinc" },
};

export function lookupBrand(id: string): BrandEntry | undefined {
  return ICON[id] ?? MONOGRAM[id];
}

const HUE_BG: Record<string, string> = {
  stone: "bg-stone-100 text-stone-900 border-stone-200",
  slate: "bg-slate-100 text-slate-900 border-slate-200",
  zinc: "bg-zinc-100 text-zinc-900 border-zinc-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  orange: "bg-orange-100 text-orange-900 border-orange-200",
  emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
  teal: "bg-teal-100 text-teal-900 border-teal-200",
  sky: "bg-sky-100 text-sky-900 border-sky-200",
  indigo: "bg-indigo-100 text-indigo-900 border-indigo-200",
  violet: "bg-violet-100 text-violet-900 border-violet-200",
  fuchsia: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200",
  rose: "bg-rose-100 text-rose-900 border-rose-200",
};

type NamedSize = "sm" | "md" | "lg" | "xl";

const SIZE_BOX: Record<NamedSize, string> = {
  sm: "size-8 text-[10px]",
  md: "size-10 text-xs",
  lg: "size-14 text-sm",
  xl: "size-20 text-base",
};

const SIZE_ICON: Record<NamedSize, string> = {
  sm: "size-4",
  md: "size-5",
  lg: "size-7",
  xl: "size-10",
};

/* Pixel equivalents of the named sizes (match the Tailwind size-* classes
   above) so company marks can be rendered through either size form. */
const BOX_PX: Record<NamedSize, number> = { sm: 32, md: 40, lg: 56, xl: 80 };
const ICON_PX: Record<NamedSize, number> = { sm: 16, md: 20, lg: 28, xl: 40 };

export interface BrandLogoProps {
  /** Experience/technology id from resume.yaml (legacy call sites). */
  id?: string;
  /** Company display name OR registry id — the prototype-port contract:
   *  `<BrandLogo company={name} size={n} bare />`. */
  company?: string;
  fallbackLabel?: string;
  /** Named tile size (legacy) or an exact pixel size (prototype-port). */
  size?: number | NamedSize;
  className?: string;
  style?: CSSProperties;
  /** When true, no surrounding tile (border / background) — render just the
   *  mark or letters. Used when the parent already provides a container
   *  (e.g., a round bubble in CareerArc or the Eras road). */
  bare?: boolean;
}

const BARE_LETTER_SIZE: Record<NamedSize, string> = {
  sm: "text-[11px]",
  md: "text-sm",
  lg: "text-base",
  xl: "text-xl",
};

export function BrandLogo({
  id,
  company,
  fallbackLabel,
  size = "md",
  className = "",
  style,
  bare = false,
}: BrandLogoProps) {
  const rawKey = company ?? id ?? "";
  const key = resolveBrandKey(rawKey);
  const title = company ?? fallbackLabel;
  const isPx = typeof size === "number";

  // ── Authored company mark (CO_LOGOS registry) ─────────────────────────
  const mark = CO_LOGOS[key];
  if (mark) {
    if (bare) {
      const px = isPx ? size : ICON_PX[size];
      return (
        <span
          className={className}
          style={{
            width: px,
            height: px,
            display: "inline-grid",
            placeItems: "center",
            overflow: "hidden",
            flex: "none",
            ...style,
          }}
          title={title}
          aria-hidden
          dangerouslySetInnerHTML={{ __html: mark }}
        />
      );
    }
    // Tile version mirrors the prototype's CompanyLogo defaults
    // (size 40, radius 11, padding ≈ 16% of the box). `.co-logo` styling
    // comes from the ported stylesheet; caller style wins over defaults.
    const px = isPx ? size : BOX_PX[size];
    return (
      <span
        className={`co-logo ${className}`}
        style={{
          width: px,
          height: px,
          borderRadius: 11,
          padding: Math.max(4, Math.round(px * 0.16)),
          ...style,
        }}
        title={title}
        aria-hidden
        dangerouslySetInnerHTML={{ __html: mark }}
      />
    );
  }

  // ── Simple-Icons / monogram fallbacks ─────────────────────────────────
  const brand = lookupBrand(key);

  // Bare = no outer tile. Parent container is responsible for sizing.
  if (bare) {
    if (brand?.Icon) {
      return (
        <brand.Icon
          className={`${isPx ? "" : SIZE_ICON[size]} ${className}`}
          style={{
            ...(brand.hex ? { color: brand.hex } : undefined),
            ...(isPx ? { width: size, height: size } : undefined),
            ...style,
          }}
          aria-hidden
        />
      );
    }
    const letters = computeLetters(brand?.letters, fallbackLabel ?? company);
    const hueText = HUE_TEXT[brand?.hue ?? "stone"];
    return (
      <span
        className={`display font-semibold tracking-tight ${hueText} ${
          isPx ? "" : BARE_LETTER_SIZE[size]
        } ${className}`}
        style={{
          ...(isPx ? { fontSize: Math.max(9, Math.round(size * 0.42)) } : undefined),
          ...style,
        }}
        aria-hidden
      >
        {letters}
      </span>
    );
  }

  // Tiled (default) — has its own border + background.
  const sizeBox = isPx ? "" : SIZE_BOX[size];
  const boxStyle: CSSProperties | undefined = isPx
    ? { width: size, height: size, ...style }
    : style;

  if (brand?.Icon) {
    return (
      <span
        className={`grid place-items-center rounded-xl border bg-white border-stone-200 ${sizeBox} ${className}`}
        style={boxStyle}
        title={title}
        aria-hidden
      >
        <brand.Icon
          className={isPx ? "" : SIZE_ICON[size]}
          style={{
            ...(brand.hex ? { color: brand.hex } : undefined),
            ...(isPx ? { width: Math.round(size * 0.5), height: Math.round(size * 0.5) } : undefined),
          }}
        />
      </span>
    );
  }

  const letters = computeLetters(brand?.letters, fallbackLabel ?? company);
  const hueClass = HUE_BG[brand?.hue ?? "stone"];

  return (
    <span
      className={`grid place-items-center rounded-xl border font-semibold tracking-tight ${hueClass} ${sizeBox} ${className}`}
      style={
        isPx
          ? { ...boxStyle, fontSize: Math.max(9, Math.round(size * 0.3)) }
          : boxStyle
      }
      title={title}
      aria-hidden
    >
      <span className="display">{letters}</span>
    </span>
  );
}

function computeLetters(brandLetters: string | undefined, fallbackLabel?: string): string {
  return (
    brandLetters ??
    (fallbackLabel
      ? fallbackLabel
          .split(/[^A-Za-z]/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0]!.toUpperCase())
          .join("")
      : "?")
  );
}

const HUE_TEXT: Record<string, string> = {
  stone: "text-stone-900",
  slate: "text-slate-900",
  zinc: "text-zinc-900",
  amber: "text-amber-900",
  orange: "text-orange-900",
  emerald: "text-emerald-900",
  teal: "text-teal-900",
  sky: "text-sky-900",
  indigo: "text-indigo-900",
  violet: "text-violet-900",
  fuchsia: "text-fuchsia-900",
  rose: "text-rose-900",
};
